import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, Building2, Globe, Mail } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Wholesale() {
  const [form, setForm] = useState({ name: "", company: "", email: "", region: "", message: "" });
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/wholesale`, form);
      setDone(true);
      toast.success("Thank you — our wholesale team will be in touch.");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="wholesale-page">
      <section className="relative bg-navy overflow-hidden">
        <div className="absolute inset-0 opacity-[0.07]" style={{ backgroundImage: "radial-gradient(circle at 1px 1px, #fff 1px, transparent 0)", backgroundSize: "22px 22px" }} />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-16 lg:py-24">
          <p className="font-mono text-[11px] uppercase tracking-[0.24em] text-denim-accent">For Retailers & Buyers</p>
          <h1 className="mt-3 font-display text-4xl sm:text-5xl lg:text-6xl font-bold uppercase tracking-tight text-white">Wholesale Inquiries</h1>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-14 lg:py-20 grid lg:grid-cols-2 gap-12">
        <div>
          <h2 className="font-display text-2xl sm:text-3xl font-bold uppercase tracking-tight">Partner with AR / American Rag</h2>
          <p className="mt-4 text-muted-foreground leading-relaxed">
            AR / American Rag partners with retailers, boutiques and wholesale buyers across North America. Share a few details and our wholesale team will follow up with the current line sheet, pricing and ordering information.
          </p>
          <div className="mt-8 space-y-5">
            {[[Building2, "Established American brand", "A contemporary denim house with nationwide retail presence."], [Globe, "Nationwide distribution", "Flexible wholesale programs for stores of every size."], [Mail, "Dedicated support", "A wholesale team to support your buy, season after season."]].map(([Icon, t, d], i) => (
              <div key={i} className="flex gap-4">
                <div className="w-11 h-11 bg-denim-light text-denim flex items-center justify-center rounded-full shrink-0"><Icon className="w-5 h-5" /></div>
                <div><h3 className="font-heading font-bold text-sm">{t}</h3><p className="text-sm text-muted-foreground mt-0.5">{d}</p></div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-offwhite border border-border p-6 sm:p-8">
          {done ? (
            <div className="h-full flex flex-col items-center justify-center text-center py-16 gap-4">
              <div className="w-14 h-14 rounded-full bg-denim-light text-denim flex items-center justify-center"><Check className="w-7 h-7" /></div>
              <h3 className="font-display text-2xl font-bold uppercase tracking-tight">Inquiry received</h3>
              <p className="text-muted-foreground text-sm max-w-xs">Thank you for your interest in AR / American Rag. Our wholesale team will be in touch shortly.</p>
              <Link to="/" className="mt-2 inline-flex items-center gap-2 text-sm font-semibold hover:text-denim">Back to home <ArrowRight className="w-4 h-4" /></Link>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="wholesale-form" className="space-y-4">
              <h3 className="font-display text-xl font-bold uppercase tracking-wide mb-2">Request the line sheet</h3>
              <div className="grid sm:grid-cols-2 gap-4">
                <input required value={form.name} onChange={upd("name")} data-testid="wholesale-name" placeholder="Full name" className="border border-border px-4 py-3.5 text-sm outline-none focus:border-ink transition-colors bg-white" />
                <input required value={form.company} onChange={upd("company")} data-testid="wholesale-company" placeholder="Company / store" className="border border-border px-4 py-3.5 text-sm outline-none focus:border-ink transition-colors bg-white" />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <input required type="email" value={form.email} onChange={upd("email")} data-testid="wholesale-email" placeholder="Email address" className="border border-border px-4 py-3.5 text-sm outline-none focus:border-ink transition-colors bg-white" />
                <input value={form.region} onChange={upd("region")} data-testid="wholesale-region" placeholder="Region / country" className="border border-border px-4 py-3.5 text-sm outline-none focus:border-ink transition-colors bg-white" />
              </div>
              <textarea value={form.message} onChange={upd("message")} data-testid="wholesale-message" placeholder="Tell us about your store and the collections you're interested in" rows={5} className="w-full border border-border px-4 py-3.5 text-sm outline-none focus:border-ink transition-colors bg-white resize-none" />
              <button data-testid="wholesale-submit" disabled={loading} className="w-full bg-ink text-white py-4 text-xs font-bold uppercase tracking-widest hover:bg-navy transition-colors disabled:opacity-60">
                {loading ? "Sending…" : "Submit Inquiry"}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
